import { Button, Checkbox, Form, TextField, TextStyle } from "@shopify/polaris"
import React, { useEffect, useState } from "react"
import useDebounce from "../../hooks/useDebounce.js"
import { validateUrl } from "../../utils/validateUrl.js"
import { useTranslation } from "react-i18next"

export const ImportStoreForm = ({
	loading,
	onSubmit,
	productsUrl,
	setProductsUrl,
}) => {
	const [isUrlValid, setIsUrlValid] = useState(true)
	const [maximumChecked, setMaximumChecked] = useState(false)
	const [paginateChecked, setPaginateChecked] = useState(false)
	const [paginateFrom, setPaginateFrom] = useState(1)
	const [paginateTo, setPaginateTo] = useState(5)
	const [productCount, setProductCount] = useState(null)
	const debouncedProductsUrl = useDebounce(productsUrl, 1500)
	const { t } = useTranslation()

	const changeProductsUrl = (value) => {
		const validUrl = validateUrl(value)
		setProductsUrl(value)
		setProductCount(null)

		if (validUrl) {
			setIsUrlValid(true)
		} else {
			setIsUrlValid(false)
		}
	}

	const submit = () => {
		if (isUrlValid) {
			onSubmit({
				productsUrl,
				maximumChecked,
				paginateChecked,
				paginateFrom,
				paginateTo,
			})
		}
	}

	useEffect(() => {
		let cancelled = false

		const fetchMeta = async () => {
			if (!maximumChecked || !debouncedProductsUrl) return
			try {
				const storeUrl = new URL(
					debouncedProductsUrl.startsWith("https://")
						? debouncedProductsUrl
						: debouncedProductsUrl.startsWith("http://")
							? debouncedProductsUrl.replace(
									"http://",
									"https://",
								)
							: `https://${debouncedProductsUrl}`,
				)
				const fetchUrl =
					`${storeUrl.origin}${storeUrl.pathname}`.replace(/\/$/, "")

				const res = await fetch(`${fetchUrl}/meta.json`)
				const json = await res.json()

				if (cancelled) return

				if (json?.collection?.products_count) {
					setProductCount(json.collection.products_count)
				} else if (json?.published_products_count) {
					setProductCount(json.published_products_count)
				} else {
					setProductCount(null)
				}
			} catch (error) {
				console.error(error)
			}
		}

		fetchMeta()

		return () => {
			cancelled = true
		}
	}, [debouncedProductsUrl, maximumChecked])

	return (
		<Form onSubmit={!loading && isUrlValid ? submit : () => void 0}>
			<TextField
				label={t("multiple.store_or_collection_url")}
				inputMode="url"
				clearButton
				onClearButtonClick={() => setProductsUrl("")}
				placeholder={t("multiple.store_or_store_collection")}
				value={productsUrl}
				onChange={changeProductsUrl}
				connectedRight={
					<Button
						key={loading ? "loading" : "idle"}
						onClick={submit}
						loading={loading}
						disabled={loading || !isUrlValid}
					>
						{t("multiple.import_products")}
					</Button>
				}
			/>

			<div style={{ marginTop: "1rem" }}>
				<Checkbox
					label={t("multiple.import_more_than_250_products")}
					helpText={t(
						"multiple.note_importing_more_than_250_products",
					)}
					checked={maximumChecked}
					onChange={() => setMaximumChecked((prev) => !prev)}
				/>

				{maximumChecked && (
					<div>
						<Checkbox
							label={t("multiple.limit_results_by_pages")}
							checked={paginateChecked}
							onChange={() => setPaginateChecked((prev) => !prev)}
						/>

						<div
							style={{
								display: "flex",
								gap: "0.5rem",
								paddingLeft: "1.8rem",
								color: "#777",
								fontSize: "0.9rem",
							}}
						>
							<span>{t("multiple.from_page")}</span>
							<input
								type="number"
								min={1}
								max={paginateTo - 1}
								value={paginateFrom}
								onChange={(change) =>
									setPaginateFrom(
										parseInt(change.target.value),
									)
								}
								style={{
									border: "none",
									background: "#eee",
									borderRadius: "0.3125rem",
									padding: "0rem 0.3rem",
									width: "3.125rem",
								}}
							></input>
							<span>{t("multiple.through_page")}</span>
							<input
								type="number"
								min={paginateFrom + 1}
								value={paginateTo}
								onChange={(change) =>
									setPaginateTo(parseInt(change.target.value))
								}
								style={{
									border: "none",
									background: "#eee",
									borderRadius: "0.3125rem",
									padding: "0rem 0.3rem",
									width: "3.125rem",
								}}
							></input>
						</div>

						<div
							style={{
								paddingLeft: "1.8rem",
								color: "#777",
								fontSize: "0.9rem",
							}}
						>
							<span>
								{t("multiple.every_page_contain_250_products")}{" "}
								{productCount &&
									Number.isInteger(productCount) &&
									`This store/collection has ${productCount} products (${Math.ceil(productCount / 250)} page(s))`}
							</span>
						</div>

						{paginateTo - paginateFrom > 9 && (
							<div style={{ marginTop: "0.5rem" }}>
								<TextStyle variation="negative">
									{t("multiple.we_recommend_paginating")}
								</TextStyle>
							</div>
						)}
					</div>
				)}
			</div>

			{!isUrlValid ? (
				<div style={{ marginTop: "2rem" }}>
					<TextStyle variation="negative">
						{t("multiple.url_is_invalid")}
					</TextStyle>
				</div>
			) : (
				""
			)}
		</Form>
	)
}
